/**
 * Vendored from original golden master:
 *   Chronovela-original-audit/original-golden-master/services/gemini.ts
 * Source SHA-256: 93015267F44D6CD41DCDF6EA03F60AE322C58A5E3B30EA29EEC8BE587F784D1F
 *
 * Transport is injected via bindGoldenGenerateFn — no direct browser Gemini calls.
 */

import { Type, ThinkingLevel } from "@google/genai";
import type {
  Ledger,
  ValidationResult,
  Chapter,
  StorySeed,
  EditorResult,
  StoryBible,
  StoryMode,
  ChapterPlan,
  PublicationEditorResult,
  SoftStateBrief,
  BookArcContract,
  ChapterProgressionContract,
  CharacterArcStep,
  NarrativeStateDelta,
  ProgressionValidation,
  NarrativeDensityMetrics,
  RoadmapProgressionIssue,
} from "../../types";
import { GEMINI_MODEL_PRO, GEMINI_MODEL_FLASH, SYSTEM_INSTRUCTIONS } from "./constants";
import { jsonrepair } from "jsonrepair";
import { extractAndParseJSON } from "./goldenJson";
import {
  bindGoldenGenerateFn,
  getBoundGoldenGenerateFn,
  goldenGenerateWithRetry as generateWithRetry,
} from "./goldenGenerateWithRetry";
export { bindGoldenGenerateFn, getBoundGoldenGenerateFn } from "./goldenGenerateWithRetry";

export const HUMAN_PROSE_CONSTRAINT = `CRITICAL HUMAN PROSE REQUIREMENT:
You must strictly avoid "AI-sounding" prose patterns.
- Do NOT use exact measurements (e.g., "three inches left", "forty-two degrees") unless contextually required.
- Do NOT overuse atmosphere/tone words like: physical weight, clinical, sterile, geometric, brutal, absolute, ancient, oppressive, mathematical, mechanical, calculus, economy.
- Do NOT use technical/physics terminology for action mechanics (e.g., center of gravity, kinetic transfer, torque, dominant base, friction lock, spatial geometry).
- Do NOT write dialogue that sounds like characters explaining the theme.
- Do NOT write long System or UI-style exposition dumps.
Write naturally, elegantly, and human-like. Focus on grounded, organic storytelling rather than clinical descriptions.`;

export const WORKER_CONFIG = {
  ARCHITECT_ROADMAP: { model: GEMINI_MODEL_PRO, thinkingLevel: ThinkingLevel.HIGH },
  CHAPTER_CONTRACT: { model: GEMINI_MODEL_PRO, thinkingLevel: ThinkingLevel.MEDIUM },
  SCRIBE: { model: GEMINI_MODEL_PRO, thinkingLevel: ThinkingLevel.HIGH },
  DIRECTOR_REWRITE: { model: GEMINI_MODEL_PRO, thinkingLevel: ThinkingLevel.HIGH },
  INSPECTOR: { model: GEMINI_MODEL_FLASH, thinkingLevel: ThinkingLevel.MEDIUM },
  SOFT_STATE: { model: GEMINI_MODEL_FLASH, thinkingLevel: ThinkingLevel.LOW },
  EDITOR: { model: GEMINI_MODEL_FLASH, thinkingLevel: ThinkingLevel.LOW },
  PUBLICATION_EDITOR: { model: GEMINI_MODEL_FLASH, thinkingLevel: ThinkingLevel.LOW },
  ARCHIVIST: { model: GEMINI_MODEL_FLASH, thinkingLevel: ThinkingLevel.LOW },
  SEQUEL_EVOLUTION: { model: GEMINI_MODEL_FLASH, thinkingLevel: ThinkingLevel.LOW }
};

export const initializeStoryState = async (seed: StorySeed): Promise<Ledger> => {
  const genreString = (seed.genre || []).map(g => `${g.name} (${g.weight})`).join(', ');
  const premiseString = `
    SURFACE CONFLICT: ${seed.premise.surface}
    UNDERLYING TENSION: ${seed.premise.tension}
    IRREVERSIBLE CHANGE: ${seed.premise.change}
  `;

  const prompt = `
    Initialize the Master Ledger for a ${seed.mode} project.
    TITLE: ${seed.title}
    GENRE MATRIX: ${genreString}
    PREMISE DNA: ${premiseString}
    CHARACTER DOSSIERS: ${seed.characters}
    WORLD LORE: ${seed.worldLore}
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.ARCHIVIST.model,
    contents: prompt,
    config: {
      systemInstruction: SYSTEM_INSTRUCTIONS.ARCHIVIST + "\nInitialize a new ledger based on the provided story setup.",
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          characters: { type: Type.OBJECT },
          locations: { type: Type.OBJECT },
          inventory: { type: Type.ARRAY, items: { type: Type.STRING } },
          plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
          momentum: { type: Type.STRING },
          arc_stage: { type: Type.STRING },
          latestStateDelta: {
            type: Type.OBJECT,
            properties: {
              gained: { type: Type.ARRAY, items: { type: Type.STRING } },
              lost: { type: Type.ARRAY, items: { type: Type.STRING } },
              damaged: { type: Type.ARRAY, items: { type: Type.STRING } },
              revealed: { type: Type.ARRAY, items: { type: Type.STRING } },
              relationshipsChanged: { type: Type.ARRAY, items: { type: Type.STRING } },
              goalsChanged: { type: Type.ARRAY, items: { type: Type.STRING } },
              threatsEscalated: { type: Type.ARRAY, items: { type: Type.STRING } },
              unresolvedConsequences: { type: Type.ARRAY, items: { type: Type.STRING } }
            },
            required: ["gained", "lost", "damaged", "revealed", "relationshipsChanged", "goalsChanged", "threatsEscalated", "unresolvedConsequences"]
          }
        },
        required: ["characters", "locations", "inventory", "plot_points", "momentum", "arc_stage", "latestStateDelta"]
      },
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHIVIST.thinkingLevel }
    },
  });

  const rawData = extractAndParseJSON(response.text || "{}");
  return {
    characters: rawData.characters || {},
    locations: rawData.locations || {},
    inventory: Array.isArray(rawData.inventory) ? rawData.inventory : [],
    plot_points: Array.isArray(rawData.plot_points) ? rawData.plot_points : [],
    resolved_plot_points: Array.isArray(rawData.resolved_plot_points) ? rawData.resolved_plot_points : [],
    momentum: rawData.momentum || "Neutral",
    arc_stage: rawData.arc_stage || "Beginning",
    current_time: rawData.current_time || "Day 1",
    last_updated: new Date().toISOString()
  };
};

export const evolveLedgerForSequel = async (currentLedger: Ledger, bible: StoryBible): Promise<Ledger> => {
  const prompt = `
    EVOLVE LEDGER FOR VOLUME ${bible.volume + 1}.
    Current state of the world: ${JSON.stringify(currentLedger)}
    
    1. Archive all plot_points and resolved_plot_points from the previous volume into 'historical_events'. Clear plot_points and resolved_plot_points.
    2. Update character descriptions based on their development in the previous arc.
    3. Reset momentum and arc_stage for a new story beginning.
    4. Keep the world lore intact but update current locations if they changed.
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.SEQUEL_EVOLUTION.model,
    contents: prompt,
    config: {
      systemInstruction: SYSTEM_INSTRUCTIONS.ARCHIVIST + "\nPerform a clean multi-book transition. Ensure continuity is preserved while creating a fresh starting state for a new volume.",
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          characters: { type: Type.OBJECT },
          locations: { type: Type.OBJECT },
          inventory: { type: Type.ARRAY, items: { type: Type.STRING } },
          plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
          resolved_plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
          historical_events: { type: Type.ARRAY, items: { type: Type.STRING } },
          momentum: { type: Type.STRING },
          arc_stage: { type: Type.STRING },
          latestStateDelta: {
            type: Type.OBJECT,
            properties: {
              gained: { type: Type.ARRAY, items: { type: Type.STRING } },
              lost: { type: Type.ARRAY, items: { type: Type.STRING } },
              damaged: { type: Type.ARRAY, items: { type: Type.STRING } },
              revealed: { type: Type.ARRAY, items: { type: Type.STRING } },
              relationshipsChanged: { type: Type.ARRAY, items: { type: Type.STRING } },
              goalsChanged: { type: Type.ARRAY, items: { type: Type.STRING } },
              threatsEscalated: { type: Type.ARRAY, items: { type: Type.STRING } },
              unresolvedConsequences: { type: Type.ARRAY, items: { type: Type.STRING } }
            },
            required: ["gained", "lost", "damaged", "revealed", "relationshipsChanged", "goalsChanged", "threatsEscalated", "unresolvedConsequences"]
          }
        },
        required: ["characters", "locations", "inventory", "plot_points", "momentum", "arc_stage", "latestStateDelta"]
      },
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.SEQUEL_EVOLUTION.thinkingLevel }
    },
  });

  const rawData = extractAndParseJSON(response.text || "{}");
  
  const inventory = Array.isArray(rawData.inventory) ? rawData.inventory : currentLedger.inventory || [];
  const plot_points = Array.isArray(rawData.plot_points) ? rawData.plot_points : [];
  const resolved_plot_points = Array.isArray(rawData.resolved_plot_points) ? rawData.resolved_plot_points : [];
  const historical_events = Array.isArray(rawData.historical_events) ? rawData.historical_events : [];

  return {
    ...currentLedger,
    ...rawData,
    inventory,
    plot_points,
    resolved_plot_points,
    historical_events,
    characters: {
      ...(currentLedger.characters || {}),
      ...(rawData.characters || {})
    },
    locations: {
      ...(currentLedger.locations || {}),
      ...(rawData.locations || {})
    },
    last_updated: new Date().toISOString()
  };
};

export const evolveBibleForSequel = async (currentBible: StoryBible): Promise<StoryBible> => {
  const prompt = `
    EVOLVE STORY BIBLE FOR VOLUME ${currentBible.volume + 1}.
    Current Premise:
    SURFACE: ${currentBible.premise.surface}
    TENSION: ${currentBible.premise.tension}
    CHANGE: ${currentBible.premise.change}

    1. Propose a new Premise for the sequel. The new premise should build upon the previous volume's resolution but introduce a new central conflict.
    2. Retain the title, genre, style guide, mode, target chapter count, and chapter length.
    3. Clear out any old chapter plans.
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.SEQUEL_EVOLUTION.model,
    contents: prompt,
    config: {
      systemInstruction: SYSTEM_INSTRUCTIONS.ARCHITECT + "\nYou are planning the next volume in a series. Create a compelling new premise.",
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          premise: {
            type: Type.OBJECT,
            properties: {
              surface: { type: Type.STRING },
              tension: { type: Type.STRING },
              change: { type: Type.STRING }
            },
            required: ["surface", "tension", "change"]
          }
        },
        required: ["premise"]
      },
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.SEQUEL_EVOLUTION.thinkingLevel }
    },
  });

  const rawData = extractAndParseJSON(response.text || "{}");

  return {
    ...currentBible,
    volume: currentBible.volume + 1,
    premise: rawData.premise || currentBible.premise,
    chapterPlans: [], // Clear old plans
    isClosingMode: false
  };
};

export const generateBookArcContract = async (
  bible: StoryBible,
  ledger: Ledger,
  onProgress?: (msg: string) => void
): Promise<BookArcContract> => {
  if (onProgress) onProgress("Synthesizing Book Arc Contract...");
  
  const instruction = SYSTEM_INSTRUCTIONS.ARCHITECT.replace('{{MODE}}', bible.mode) + "\nYou are a Narrative Hardening layer. Ensure intense causality and structural arc.";
  const prompt = `
Generate a BookArcContract for the project: ${bible.title}.
PREMISE: ${JSON.stringify(bible.premise)}
TARGET CHAPTERS: ${bible.targetChapterCount}
LEDGER: ${JSON.stringify(ledger)}
${bible.premadeRoadmap ? "PREMADE ROADMAP PROVIDED:\n" + bible.premadeRoadmap : ""}
`;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.ARCHITECT_ROADMAP.model,
    contents: prompt,
    config: {
      systemInstruction: instruction,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHITECT_ROADMAP.thinkingLevel },
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          protagonistExternalGoal: { type: Type.STRING },
          protagonistInternalLie: { type: Type.STRING },
          protagonistNeed: { type: Type.STRING },
          primaryOpposition: { type: Type.STRING },
          centralStakes: { type: Type.STRING },
          irreversibleFailureCondition: { type: Type.STRING },
          openingState: { type: Type.STRING },
          incitingDisruption: { type: Type.STRING },
          firstMajorCommitment: { type: Type.STRING },
          midpointReversal: { type: Type.STRING },
          falseVictoryOrMajorDefeat: { type: Type.STRING },
          crisisChoice: { type: Type.STRING },
          climaxDecision: { type: Type.STRING },
          endingTransformation: { type: Type.STRING },
          relationshipArc: {
            type: Type.OBJECT,
            properties: {
              relationship: { type: Type.STRING },
              openingState: { type: Type.STRING },
              majorFracture: { type: Type.STRING },
              turningPoint: { type: Type.STRING },
              endingState: { type: Type.STRING }
            }
          },
          escalationStages: { type: Type.ARRAY, items: { type: Type.STRING } }
        },
        required: [
          "protagonistExternalGoal", "protagonistInternalLie", "protagonistNeed", "primaryOpposition",
          "centralStakes", "irreversibleFailureCondition", "openingState", "incitingDisruption",
          "firstMajorCommitment", "midpointReversal", "falseVictoryOrMajorDefeat", "crisisChoice",
          "climaxDecision", "endingTransformation", "escalationStages"
        ]
      }
    }
  });

  return extractAndParseJSON(response.text || "{}") as BookArcContract;
};

export const validateRoadmapProgression = async (
  bible: StoryBible,
  plans: ChapterPlan[]
): Promise<RoadmapProgressionIssue[]> => {
  if (plans.length < 2) return [];

  const instruction = SYSTEM_INSTRUCTIONS.ARCHITECT.replace('{{MODE}}', bible.mode) + "\nYou are a Narrative Hardening layer. Ensure intense causality and structural arc.";
  const prompt = `
Validate the causality and progression of the proposed Chapter Roadmap for ${bible.title}.
PREMISE: ${JSON.stringify(bible.premise)}
TARGET CHAPTERS: ${bible.targetChapterCount}

For each Chapter from Chapter 2 onwards, analyze the chapter's "progression" to see if it is strictly caused by the previous chapter's events.
Also flag chapters with NO_PERMANENT_CHANGE or STATUS_QUO_RESET.

ROADMAP:
${JSON.stringify(plans.map(p => ({
  chapterNumber: p.chapterNumber,
  title: p.title,
  summary: p.summary,
  progression: p.progression
})))}

Provide any progression issues found. If none, return an empty array.
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.ARCHITECT_ROADMAP.model,
    contents: prompt,
    config: {
      systemInstruction: instruction,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHITECT_ROADMAP.thinkingLevel },
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          issues: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                chapterNumber: { type: Type.NUMBER },
                code: { type: Type.STRING },
                explanation: { type: Type.STRING },
                suggestedRepair: { type: Type.STRING }
              },
              required: ["chapterNumber", "code", "explanation", "suggestedRepair"]
            }
          }
        },
        required: ["issues"]
      }
    }
  });

  const parsed = extractAndParseJSON(response.text || "{}");
  return Array.isArray(parsed.issues) ? parsed.issues : [];
};

export const planChapters = async (
  bible: StoryBible, 
  ledger: Ledger,
  onProgress?: (msg: string) => void
): Promise<ChapterPlan[]> => {
  const genreString = (bible.genre || []).map(g => `${g.name} (${g.weight})`).join(', ');
  const premiseString = `
    SURFACE: ${bible.premise.surface}
    TENSION: ${bible.premise.tension}
    CHANGE: ${bible.premise.change}
  `;

  const instruction = SYSTEM_INSTRUCTIONS.ARCHITECT.replace('{{MODE}}', bible.mode);
  
  const targetCount = bible.targetChapterCount;
  const BATCH_SIZE = 10;
  let allPlans: ChapterPlan[] = [];

  for (let startIdx = 1; startIdx <= targetCount; startIdx += BATCH_SIZE) {
    const endIdx = Math.min(startIdx + BATCH_SIZE - 1, targetCount);
    
    if (onProgress) {
      onProgress(`Synthesizing Roadmap chunk: Chapters ${startIdx} to ${endIdx}...`);
    }

    const previousContext = allPlans.length > 0 
      ? `PREVIOUS CHAPTERS:\n${allPlans.map(p => `CH ${p.chapterNumber}: ${p.title}\n${p.summary}`).join('\n\n')}`
      : 'NO PREVIOUS CHAPTERS YET.';

    const premadeRoadmapContext = bible.premadeRoadmap 
      ? `IMPORTANT PRE-MADE ROADMAP: The user has explicitly provided the following custom roadmap. You MUST adhere to the overarching arc outlined here rather than inventing your own completely new arc. Use it as the source of truth for the plot: \n\n${bible.premadeRoadmap}\n\n`
      : '';

    const prompt = `
      PLAN CHAPTERS ${startIdx} to ${endIdx} (out of ${targetCount}) for a ${bible.mode} project.
      TITLE: ${bible.title}
      GENRES: ${genreString}
      PREMISE: ${premiseString}
      STYLE GUIDE: ${bible.style_guide}
      INITIAL LEDGER: ${JSON.stringify(ledger)}
      
      ${premadeRoadmapContext}
      ${previousContext}
      
      Your task is to generate the plans ONLY for chapters ${startIdx} through ${endIdx}.
      Ensure they seamlessly continue from the previous chapters (if any) and move towards a satisfying conclusion at chapter ${targetCount}.
      
      CRITICAL REMINDER: You must ensure all characters from the INITIAL LEDGER are utilized across the entire roadmap. Avoid focusing entirely on the protagonist at the expense of supporting characters. Double-check that all established characters are woven into the chapter plans you are generating.
    `;

    const response = await generateWithRetry({
      model: WORKER_CONFIG.ARCHITECT_ROADMAP.model,
      contents: prompt,
      config: {
        systemInstruction: instruction,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            plans: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  chapterNumber: { type: Type.NUMBER },
                  title: { type: Type.STRING },
                  pov: { type: Type.STRING },
                  summary: { type: Type.STRING },
                  requiredBeats: { type: Type.ARRAY, items: { type: Type.STRING } },
                  forbiddenMoves: { type: Type.ARRAY, items: { type: Type.STRING } },
                  continuityNotes: { type: Type.ARRAY, items: { type: Type.STRING } },
                  characterFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
                  locationFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
                  progression: {
                    type: Type.OBJECT,
                    properties: {
                      immediateGoal: { type: Type.STRING },
                      activeOpposition: { type: Type.STRING },
                      protagonistDecision: { type: Type.STRING },
                      decisionCost: { type: Type.STRING },
                      permanentChange: { type: Type.STRING },
                      externalArcAdvance: { type: Type.STRING },
                      internalArcPressure: { type: Type.STRING },
                      relationshipShift: { type: Type.STRING },
                      newComplication: { type: Type.STRING },
                      carryForwardPressure: { type: Type.STRING },
                      cannotResetToPriorState: { type: Type.BOOLEAN }
                    },
                    required: ["immediateGoal", "activeOpposition", "protagonistDecision", "decisionCost", "permanentChange", "externalArcAdvance", "internalArcPressure", "newComplication", "carryForwardPressure", "cannotResetToPriorState"]
                  },
                  characterArcStep: {
                    type: Type.OBJECT,
                    properties: {
                      beliefAtStart: { type: Type.STRING },
                      pressureApplied: { type: Type.STRING },
                      reaction: { type: Type.STRING },
                      evidenceBeliefIsFailing: { type: Type.STRING },
                      beliefAtEnd: { type: Type.STRING }
                    },
                    required: ["beliefAtStart", "pressureApplied", "reaction", "evidenceBeliefIsFailing", "beliefAtEnd"]
                  }
                },
                required: ["chapterNumber", "title", "summary", "requiredBeats", "progression", "characterArcStep"]
              }
            }
          },
          required: ["plans"]
        },
        thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHITECT_ROADMAP.thinkingLevel },
      },
    });

    const rawData = extractAndParseJSON(response.text || "{}");
    const plans = Array.isArray(rawData.plans) ? rawData.plans : [];
    
    // Assign proper IDs to new plans
    const newPlansToAppend = plans.map((p: any) => ({
      ...p,
      id: `plan_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      status: "planned",
      updatedAt: new Date().toISOString()
    }));
    
    allPlans = [...allPlans, ...newPlansToAppend];
  }

  // Ensure all plans are sequentially numbered properly for what we asked.
  // Wait, if AI missed some, let's just make sure final plans covers 1 to targetCount exactly.
  const finalPlans: ChapterPlan[] = [];
  for(let i=1; i<=targetCount; i++) {
    const generated = allPlans.find(p => p.chapterNumber === i);
    if (generated) {
      finalPlans.push(generated);
    } else {
      finalPlans.push({
        id: `plan_fallback_${Date.now()}_${i}`,
        chapterNumber: i,
        title: `Chapter ${i}`,
        summary: 'Advance the plot.',
        requiredBeats: [],
        status: 'planned' as const,
        updatedAt: new Date().toISOString()
      });
    }
  }

  return finalPlans;
};

export const regenerateRemainingRoadmap = async (
  bible: StoryBible, 
  ledger: Ledger, 
  existingPlans: ChapterPlan[], 
  fromIndex: number,
  targetChapterCount: number,
  onProgress?: (msg: string) => void
): Promise<ChapterPlan[]> => {
  const genreString = (bible.genre || []).map(g => `${g.name} (${g.weight})`).join(', ');
  const premiseString = `
    SURFACE: ${bible.premise.surface}
    TENSION: ${bible.premise.tension}
    CHANGE: ${bible.premise.change}
  `;

  const safePlansMap = new Map<number, ChapterPlan>();
  existingPlans.forEach((p, idx) => {
    if (idx < fromIndex || p.status === 'locked' || p.status === 'drafted') {
      safePlansMap.set(p.chapterNumber, p);
    }
  });

  const instruction = SYSTEM_INSTRUCTIONS.ARCHITECT.replace('{{MODE}}', bible.mode);
  
  const BATCH_SIZE = 10;
  let newlyGeneratedPlans: any[] = [];
  
  // Find all chapters that actually need generation
  const chaptersToGenerate: number[] = [];
  for (let i = 1; i <= targetChapterCount; i++) {
    if (!safePlansMap.has(i)) {
      chaptersToGenerate.push(i);
    }
  }

  // Iterate over batches
  for (let i = 0; i < chaptersToGenerate.length; i += BATCH_SIZE) {
    const currentBatch = chaptersToGenerate.slice(i, i + BATCH_SIZE);
    const startIdx = currentBatch[0];
    const endIdx = currentBatch[currentBatch.length - 1];

    if (onProgress) {
      onProgress(`Regenerating Roadmap chunk: Chapters ${startIdx} to ${endIdx}...`);
    }

    const lockedContext = safePlansMap.size > 0 
      ? `LOCKED INCORPORATED CHAPTERS:\n${Array.from(safePlansMap.values()).sort((a,b) => a.chapterNumber - b.chapterNumber).map(p => `CH ${p.chapterNumber}: ${p.title}\n${p.summary}`).join('\n\n')}` 
      : 'NO LOCKED CHAPTERS.';
      
    const recentContext = newlyGeneratedPlans.length > 0 
      ? `RECENTLY GENERATED CHAPTERS IN THIS RUN:\n${newlyGeneratedPlans.map(p => `CH ${p.chapterNumber}: ${p.title}\n${p.summary}`).join('\n\n')}`
      : 'NO RECENTLY GENERATED CHAPTERS YET.';

    const premadeRoadmapContext = bible.premadeRoadmap 
      ? `IMPORTANT PRE-MADE ROADMAP: The user has explicitly provided the following custom roadmap. Adhere to it: \n\n${bible.premadeRoadmap}\n\n`
      : '';

    const prompt = `
      You are the Architect. The user wants to regenerate their chapter roadmap.
      
      PROJECT GOAL: A continuous story of EXACTLY ${targetChapterCount} chapters.
      
      TITLE: ${bible.title}
      GENRES: ${genreString}
      PREMISE: ${premiseString}
      STYLE GUIDE: ${bible.style_guide}
      INITIAL LEDGER: ${JSON.stringify(ledger)}
      
      ${premadeRoadmapContext}
      ${lockedContext}
      
      ${recentContext}
      
      Your task:
      Generate plans ONLY for the following specific chapters: ${currentBatch.join(', ')}.
      Ensure the arc connects perfectly with the locked and recently generated plans, resolving compellingly by the last chapter.
      Output ONLY the plans for these specific requested chapters.
      For each requested chapter, provide its chapterNumber, title, pov, summary, and requiredBeats.
      
      CRITICAL REMINDER: You must ensure all characters from the INITIAL LEDGER are utilized across the remaining roadmap. Avoid focusing entirely on the protagonist at the expense of supporting characters. Double-check that all established characters are woven into these new chapter plans.
    `;

    const response = await generateWithRetry({
      model: WORKER_CONFIG.ARCHITECT_ROADMAP.model,
      contents: prompt,
      config: {
        systemInstruction: instruction,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            plans: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  chapterNumber: { type: Type.NUMBER },
                  title: { type: Type.STRING },
                  pov: { type: Type.STRING },
                  summary: { type: Type.STRING },
                  requiredBeats: { type: Type.ARRAY, items: { type: Type.STRING } },
                  forbiddenMoves: { type: Type.ARRAY, items: { type: Type.STRING } },
                  continuityNotes: { type: Type.ARRAY, items: { type: Type.STRING } },
                  characterFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
                  locationFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
                  progression: {
                    type: Type.OBJECT,
                    properties: {
                      immediateGoal: { type: Type.STRING },
                      activeOpposition: { type: Type.STRING },
                      protagonistDecision: { type: Type.STRING },
                      decisionCost: { type: Type.STRING },
                      permanentChange: { type: Type.STRING },
                      externalArcAdvance: { type: Type.STRING },
                      internalArcPressure: { type: Type.STRING },
                      relationshipShift: { type: Type.STRING },
                      newComplication: { type: Type.STRING },
                      carryForwardPressure: { type: Type.STRING },
                      cannotResetToPriorState: { type: Type.BOOLEAN }
                    },
                    required: ["immediateGoal", "activeOpposition", "protagonistDecision", "decisionCost", "permanentChange", "externalArcAdvance", "internalArcPressure", "newComplication", "carryForwardPressure", "cannotResetToPriorState"]
                  },
                  characterArcStep: {
                    type: Type.OBJECT,
                    properties: {
                      beliefAtStart: { type: Type.STRING },
                      pressureApplied: { type: Type.STRING },
                      reaction: { type: Type.STRING },
                      evidenceBeliefIsFailing: { type: Type.STRING },
                      beliefAtEnd: { type: Type.STRING }
                    },
                    required: ["beliefAtStart", "pressureApplied", "reaction", "evidenceBeliefIsFailing", "beliefAtEnd"]
                  }
                },
                required: ["chapterNumber", "title", "summary", "requiredBeats", "progression", "characterArcStep"]
              }
            }
          },
          required: ["plans"]
        },
        thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHITECT_ROADMAP.thinkingLevel },
      },
    });

    const rawData = extractAndParseJSON(response.text || "{}");
    const plans = Array.isArray(rawData.plans) ? rawData.plans : [];
    
    const mappedPlans = plans.map((p: any) => ({
      ...p,
      id: `plan_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      status: "planned",
      updatedAt: new Date().toISOString()
    }));
    
    newlyGeneratedPlans = [...newlyGeneratedPlans, ...mappedPlans];
  }

  const finalPlans: ChapterPlan[] = [];
  for (let i = 1; i <= targetChapterCount; i++) {
    if (safePlansMap.has(i)) {
      finalPlans.push(safePlansMap.get(i)!);
    } else {
      const generated = newlyGeneratedPlans.find(p => p.chapterNumber === i);
      if (generated) {
        finalPlans.push(generated);
      } else {
        // Fallback if AI missed it
        finalPlans.push({
          id: `plan_fallback_${Date.now()}_${i}`,
          chapterNumber: i,
          title: `Chapter ${i}`,
          summary: 'Advance the plot.',
          requiredBeats: [],
          status: 'planned' as const,
          updatedAt: new Date().toISOString()
        });
      }
    }
  }

  return finalPlans;
};

export const regenerateSingleChapterPlan = async (
  bible: StoryBible,
  ledger: Ledger,
  existingPlans: ChapterPlan[],
  targetIndex: number
): Promise<ChapterPlan> => {
  const genreString = (bible.genre || []).map(g => `${g.name} (${g.weight})`).join(', ');
  const premiseString = `
    SURFACE: ${bible.premise.surface}
    TENSION: ${bible.premise.tension}
    CHANGE: ${bible.premise.change}
  `;

  const previousPlans = existingPlans.slice(0, targetIndex);
  const futurePlans = existingPlans.slice(targetIndex + 1);
  const targetChapterNum = targetIndex + 1;

  const premadeRoadmapContext = bible.premadeRoadmap 
    ? `IMPORTANT PRE-MADE ROADMAP: The user has explicitly provided the following custom roadmap. Connect the arc according to it: \n\n${bible.premadeRoadmap}\n\n`
    : '';

  const prompt = `
    You are the Architect. The user wants to perfectly regenerate ONE specific chapter plan (Chapter ${targetChapterNum}) to bridge the gap between the established previous chapters and the planned future chapters.
    
    TITLE: ${bible.title}
    GENRES: ${genreString}
    PREMISE: ${premiseString}
    STYLE GUIDE: ${bible.style_guide}
    INITIAL LEDGER: ${JSON.stringify(ledger)}
    
    ${premadeRoadmapContext}
    
    PREVIOUS CANON PLANS:
    ${previousPlans.map(p => `CH ${p.chapterNumber}: ${p.title} - ${p.summary}`).join('\n')}
    
    FUTURE DESTINATION PLANS:
    ${futurePlans.map(p => `CH ${p.chapterNumber}: ${p.title} - ${p.summary}`).join('\n')}
    
    Your task:
    Generate exactly ONE chapter plan for Chapter ${targetChapterNum} that seamlessly connects the PREVIOUS CANON to the FUTURE DESTINATION. The required beats must advance the character and story meaningfully toward the destination.
    Output ONLY the JSON for this single chapter.
  `;

  const instruction = SYSTEM_INSTRUCTIONS.ARCHITECT.replace('{{MODE}}', bible.mode);

  const response = await generateWithRetry({
    model: WORKER_CONFIG.ARCHITECT_ROADMAP.model,
    contents: prompt,
    config: {
      systemInstruction: instruction,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          chapterNumber: { type: Type.NUMBER },
          title: { type: Type.STRING },
          pov: { type: Type.STRING },
          summary: { type: Type.STRING },
          requiredBeats: { type: Type.ARRAY, items: { type: Type.STRING } },
          forbiddenMoves: { type: Type.ARRAY, items: { type: Type.STRING } },
          continuityNotes: { type: Type.ARRAY, items: { type: Type.STRING } },
          characterFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
          locationFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
                  progression: {
                    type: Type.OBJECT,
                    properties: {
                      immediateGoal: { type: Type.STRING },
                      activeOpposition: { type: Type.STRING },
                      protagonistDecision: { type: Type.STRING },
                      decisionCost: { type: Type.STRING },
                      permanentChange: { type: Type.STRING },
                      externalArcAdvance: { type: Type.STRING },
                      internalArcPressure: { type: Type.STRING },
                      relationshipShift: { type: Type.STRING },
                      newComplication: { type: Type.STRING },
                      carryForwardPressure: { type: Type.STRING },
                      cannotResetToPriorState: { type: Type.BOOLEAN }
                    },
                    required: ["immediateGoal", "activeOpposition", "protagonistDecision", "decisionCost", "permanentChange", "externalArcAdvance", "internalArcPressure", "newComplication", "carryForwardPressure", "cannotResetToPriorState"]
                  },
                  characterArcStep: {
                    type: Type.OBJECT,
                    properties: {
                      beliefAtStart: { type: Type.STRING },
                      pressureApplied: { type: Type.STRING },
                      reaction: { type: Type.STRING },
                      evidenceBeliefIsFailing: { type: Type.STRING },
                      beliefAtEnd: { type: Type.STRING }
                    },
                    required: ["beliefAtStart", "pressureApplied", "reaction", "evidenceBeliefIsFailing", "beliefAtEnd"]
                  }
                },
                required: ["chapterNumber", "title", "summary", "requiredBeats", "progression", "characterArcStep"]
      },
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHITECT_ROADMAP.thinkingLevel },
    },
  });

  const parsed = extractAndParseJSON(response.text || "{}");
  return {
    ...parsed,
    id: `plan_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    status: "planned",
    updatedAt: new Date().toISOString(),
    chapterNumber: targetChapterNum
  };
};

export const retrieveSoftState = async (
  storyBible: StoryBible,
  ledger: Ledger,
  chapterNum: number,
  previousChapters: Chapter[]
): Promise<SoftStateBrief | null> => {
  if (chapterNum <= 1 || previousChapters.length === 0) return null;

  const relevantChapters = previousChapters.slice(-3); // Get last 1 to 3 chapters
  const chapterPlan = storyBible.chapterPlans?.find(p => p?.chapterNumber === chapterNum);
  const activeLocations = Object.values(ledger.locations || {}).filter((l: any) => l.status?.includes('Active')).map((l: any) => l.name);
  const activeCharacters = Object.values(ledger.characters || {}).filter((c: any) => c.status?.includes('Active') || activeLocations.includes(c.location)).map((c: any) => c.name);

  const prompt = `
    You are the Soft State Retrieval Engine for a story generation system.
    Your job is to extract recent semantic texture, atmospheric details, emotional residue, and character voice notes from the immediately preceding chapters. This helps the Scribe maintain continuity in the upcoming chapter without overriding strictly factual canon.
    
    UPCOMING CHAPTER CONTEXT:
    Chapter number: ${chapterNum}
    Active characters: ${activeCharacters.join(', ')}
    Active locations: ${activeLocations.join(', ')}
    Chapter Objective: ${chapterPlan?.summary || 'Unknown'}
    
    RECENT PRIOR CHAPTERS (Source Material):
    ${relevantChapters.map(c => `--- CHAPTER ${c.id}: ${c.title} ---\n${c.content}\n`).join('\n')}
    
    Extract a concise SoftStateBrief. Ignore massive plot points (Ledger handles that). Focus entirely on atmosphere, dialogue cadence, emotional carryover, recurring sensory cues, motifs to preserve slightly, and phrases to avoid repeating.
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.SOFT_STATE.model,
    contents: prompt,
    config: {
      systemInstruction: "You are the Soft State Memory layer. Be concise, extracting only atmosphere, voice, and sensory residue that naturally flows into the next immediate chapter. This is an aid for the Scribe.",
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.SOFT_STATE.thinkingLevel },
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          recentSensoryCarryover: { type: Type.STRING },
          activeEmotionalResidue: { type: Type.STRING },
          voiceNotes: { type: Type.STRING },
          environmentalContinuityCues: { type: Type.STRING },
          motifsToLightlyPreserve: { type: Type.STRING },
          antiRepetitionWarnings: { type: Type.STRING },
        },
        required: [
          "recentSensoryCarryover", 
          "activeEmotionalResidue", 
          "voiceNotes", 
          "environmentalContinuityCues", 
          "motifsToLightlyPreserve", 
          "antiRepetitionWarnings"
        ]
      }
    }
  });

  return extractAndParseJSON(response.text || "{}") as SoftStateBrief;
};

export const generateChapter = async (
  storyBible: StoryBible, 
  ledger: Ledger, 
  chapterNum: number, 
  prevSummary: string,
  correctionOrder?: string,
  editorFeedback?: string,
  directorsNote?: string,
  retryCount: number = 0,
  failedDraft?: string,
  softState?: SoftStateBrief | null
): Promise<Chapter> => {
  const genreString = (storyBible.genre || []).map(g => `${g.name} (${g.weight})`).join(', ');
  const premiseString = `
    SURFACE: ${storyBible.premise.surface}
    TENSION: ${storyBible.premise.tension}
    CHANGE: ${storyBible.premise.change}
  `;

  const closingInstructions = storyBible.isClosingMode 
    ? "CRITICAL: CLOSING PROTOCOL ACTIVE. You must begin resolving major plot threads. Drive the narrative toward a satisfying conclusion for this volume. Do not introduce new major mysteries." 
    : "";

  const chapterPlan = storyBible.chapterPlans?.find(p => p?.chapterNumber === chapterNum);
  const planContext = chapterPlan ? `
    CHAPTER PLAN:
    TITLE: ${chapterPlan.title}
    SUMMARY: ${chapterPlan.summary}
    REQUIRED BEATS:
    ${(chapterPlan.requiredBeats || []).map(b => `- ${b}`).join('\n    ')}
    CHARACTER FOCUS: ${(chapterPlan.characterFocus || []).join(', ')}
    LOCATION FOCUS: ${(chapterPlan.locationFocus || []).join(', ')}
    CONTINUITY NOTES: ${(chapterPlan.continuityNotes || []).join(', ')}
    FORBIDDEN MOVES: ${(chapterPlan.forbiddenMoves || []).join(', ')}
    PROGRESSION CONTRACT: ${chapterPlan.progression ? JSON.stringify(chapterPlan.progression) : 'None'}
    CHARACTER ARC STEP: ${chapterPlan.characterArcStep ? JSON.stringify(chapterPlan.characterArcStep) : 'None'}
  ` : "";

  const softStateContext = softState ? `
    SOFT STATE MEMORY (SUPPORTIVE ONLY):
    - Sensory Carryover: ${softState.recentSensoryCarryover}
    - Emotional Residue: ${softState.activeEmotionalResidue}
    - Voice Notes: ${softState.voiceNotes}
    - Environmental Cues: ${softState.environmentalContinuityCues}
    - Motifs: ${softState.motifsToLightlyPreserve}
    - ANTI-REPETITION: ${softState.antiRepetitionWarnings}
  ` : "";

  const activeLocations = Object.values(ledger.locations || {}).filter((l: any) => l.status?.includes('Active')).map((l: any) => l.name);
  const activeCharacters = Object.values(ledger.characters || {}).filter((c: any) => c.status?.includes('Active') || activeLocations.includes(c.location)).map((c: any) => c.name);

  // 2. AMNESIA BUFFER: Explicitly filter the prompt
  const ledgerKnowledge = (ledger.resolved_plot_points || [])
    .map(p => `[KNOWN: ${p}]`)
    .join(" ");

  const prompt = `
    GENERATE CHAPTER ${chapterNum} of ${storyBible.targetChapterCount} [VOLUME ${storyBible.volume}].
    
    CANON ANCHORS (SINGLE SOURCE OF TRUTH):
    - ACTIVE LOCATIONS: ${activeLocations.length > 0 ? activeLocations.join(', ') : "Unknown"}
    - ACTIVE CHARACTERS: ${activeCharacters.length > 0 ? activeCharacters.join(', ') : "Unknown"}
    - AVAILABLE CAST: ${Object.values(ledger.characters || {}).map((c: any) => c.name).join(', ')}
    - UNRESOLVED PLOT POINTS: ${ledger.plot_points?.join(', ') || "None"}
    - RESOLVED PLOT POINTS (DO NOT REPEAT): ${ledger.resolved_plot_points?.join(', ') || "None"}
    
    STRICT RULE: The characters are currently at the Active Locations. They ALREADY KNOW the Resolved Plot Points. Do NOT write them discovering, theorizing, or being shocked by these facts. Treat these facts as mundane background knowledge.
    
    STRICT HIERARCHY: Ledger > Plan > Style.
    Character Knowledge: ${ledgerKnowledge}
    DO NOT REPEAT RESOLVED POINTS.
    
    CURRENT LEDGER: ${JSON.stringify(ledger)}
    PREVIOUS CONTEXT: ${prevSummary}
    ${ledger.latestStateDelta ? `PRIOR CHAPTER DELTA (CONSEQUENCES CARRIED FORWARD): ${JSON.stringify(ledger.latestStateDelta)}` : ""}
    
    ${planContext}

    CHAPTER CONTRACT (STRICT REQUIREMENTS):
    STRICT HIERARCHY OF TRUTH:
    1. MASTER LEDGER (Absolute Canon)
    2. DIRECTOR'S DIRECTION (Immediate Priority)
    3. CHAPTER PLAN (Required Beats)

    1. VALID PROGRESS: You must advance the 'KEY EVENTS' from the Chapter Plan. Do not stall in stasis.
       - [EXACT FACTS]: Must be executed literally, exactly as written. No deviation. Do not invent unlisted mechanics or tools.
       - [REQUIRED OUTCOMES]: The end result must happen, but you can invent the staging/action to get there.
       - [OPTIONAL STAGING]: Suggestions for action/dialogue. You can use them or invent your own if it fits better.
       - [EMOTIONAL PAYOFF ONLY]: Focus on the internal/emotional realization. The physical action doesn't matter as much.
       - [FORBIDDEN REPEAT]: CRITICAL! Do NOT write this happening. It is a trap. Do not summarize it. Do not mention it. It already happened.
    2. SCENE BOUNDARIES: Remain at the CURRENT LOCATION unless the Chapter Plan explicitly requires travel. No bad cutaways.
    3. CHARACTER STATE: Focus on the ACTIVE CHARACTERS and any characters specified in the CHAPTER PLAN's character focus. You may draw from the AVAILABLE CAST if necessary to execute the plan. Do NOT invent new main characters.
    4. ABILITIES & LORE: Strictly adhere to the rules established in the Ledger. No teleportation, no unearned powers, no invented bridge mechanics.
    5. BEAT CONSUMPTION: Do not repeat events from the PREVIOUS CONTEXT or RESOLVED PLOT POINTS.
    6. CAUSALITY: Actions must have logical consequences. Characters cannot know things they haven't learned.

    NARRATIVE DNA:
    GENRES: ${genreString}
    PREMISE: ${premiseString}

    ${softStateContext}

    ${closingInstructions}
    ${directorsNote ? `DIRECTOR'S DIRECTION: ${directorsNote}` : ""}
    
    ${correctionOrder ? `
    ==================================================
    INSPECTOR'S CORRECTION ORDER (MANDATORY FIX):
    ${correctionOrder}
    
    ${failedDraft ? `PREVIOUS FAILED DRAFT (DO NOT REPEAT THESE MISTAKES):
    ${failedDraft}` : ""}
    
    CRITICAL: You are in a retry loop because your previous attempt failed validation. 
    You MUST apply the exact fixes requested in the Correction Order above. 
    IF THE CORRECTION ORDER CONFLICTS WITH THE CHAPTER PLAN, THE CORRECTION ORDER WINS.
    Do not repeat the same mistakes.
    ==================================================` : ""}
    
    ${editorFeedback ? `EDITOR NOTES: ${editorFeedback}` : ""}
    
    CRITICAL WORD COUNT INSTRUCTION:
    You are writing a ${storyBible.chapterLength} length chapter. 
    You MUST write AT LEAST the minimum word count specified in your system instructions. 
    If you are writing a STANDARD chapter, you MUST write at least 2500 words.
    If you are writing an EXTENDED chapter, you MUST write at least 4000 words.
    DO NOT summarize. DO NOT rush the ending.
    Expand every single beat with maximum detail.
    
    Generate exactly 4 distinct scenes in the scenes array. Each scene must focus on a granular micro-beat of the chapter plan. Do not rush to the next plot point. Explore the physics, the pain, and the internal processing of the characters.
    Write at least 800-1000 words PER SCENE. Do not output a single massive string.
    You will be penalized if you write less than 2500 words. Expand dialogue, internal monologue, and sensory details to hit the target.
  `;

  const instruction = SYSTEM_INSTRUCTIONS.SCRIBE
    .replace('{{MODE}}', storyBible.mode)
    .replace('{{TARGET_COUNT}}', String(storyBible.targetChapterCount))
    .replace('{{LENGTH}}', storyBible.chapterLength) + 
    `\nSTYLE GUIDE: ${storyBible.style_guide}` +
    `\n${HUMAN_PROSE_CONSTRAINT}` +
    (retryCount >= 2 && retryCount !== 4 ? "\n\nACT AS A DETERMINISTIC EXECUTION ENGINE. PRIORITIZE CONTINUITY OVER CREATIVE FLOURISH. IF A CHARACTER IS NOT AT THE CURRENT LOCATION IN THE LEDGER, THEY CANNOT APPEAR IN THIS CHAPTER." : "") +
    (retryCount >= 4 ? "\n\nCRITICAL DEADLOCK WARNING: You have failed to write this chapter multiple times. You MUST strictly adhere to the Ledger. Do NOT invent new mechanics, do NOT contradict resolved plot points, and do NOT teleport characters. Follow the Chapter Plan EXACTLY as written." : "");

  const model = WORKER_CONFIG.SCRIBE.model;
  const temperature = retryCount === 0 ? 1.0 : 
                      retryCount === 1 ? 0.7 : 
                      retryCount === 2 ? 0.6 : 
                      retryCount === 3 ? 0.5 : 
                      retryCount === 4 ? 0.7 : // Fresh start with new Architect plan
                      retryCount === 5 ? 0.6 : 
                      0.8;

  const response = await generateWithRetry({
    model,
    contents: prompt,
    config: {
      systemInstruction: instruction,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.SCRIBE.thinkingLevel },
      temperature,
      maxOutputTokens: 16384,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          scenes: {
            type: Type.ARRAY,
            description: "Generate exactly 4 distinct scenes. Each scene MUST be extremely detailed and long (at least 800 words per scene).",
            items: {
              type: Type.STRING,
            }
          },
          summary: { type: Type.STRING }
        },
        required: ["title", "scenes", "summary"]
      }
    },
  });

  const json = extractAndParseJSON(response.text || "");
  
  // Handle the new scenes array format to force longer generations
  if (json.scenes && Array.isArray(json.scenes)) {
    json.content = json.scenes.join('\n\n***\n\n');
    delete json.scenes;
  } else if (!json.content) {
    json.content = "Error: Model failed to generate content.";
  }
  
  return { id: chapterNum, ...json };
};

export const editChapter = async (chapter: Chapter, bible: StoryBible, ledger: Ledger, plan?: ChapterPlan): Promise<EditorResult> => {
  const instruction = SYSTEM_INSTRUCTIONS.EDITOR.replace('{{MODE}}', bible.mode) + `\nStyle Guide: ${bible.style_guide}\n${HUMAN_PROSE_CONSTRAINT}`;
  const planContext = plan ? `CHAPTER PLAN:
TITLE: ${plan.title}
SUMMARY: ${plan.summary}
REQUIRED BEATS:
${(plan.requiredBeats || []).map(b => `- ${b}`).join('\n')}
CHARACTER FOCUS: ${(plan.characterFocus || []).join(', ')}` : "None";

  const response = await generateWithRetry({
    model: WORKER_CONFIG.EDITOR.model,
    contents: `CURRENT LEDGER:\n${JSON.stringify(ledger)}\n\n${planContext}\n\nCritique and polish this content:\n\n${chapter.content}`,
    config: {
      systemInstruction: instruction,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.EDITOR.thinkingLevel },
      maxOutputTokens: 65536,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          status: { type: Type.STRING },
          feedback: { type: Type.STRING },
          polished_scenes: {
            type: Type.ARRAY,
            description: "The polished scenes. Maintain the length of the original scenes. Do not summarize or shorten them.",
            items: { type: Type.STRING }
          }
        },
        required: ["status", "feedback", "polished_scenes"]
      }
    },
  });
  const res = extractAndParseJSON(response.text || "");
  
  let polishedText = res.polished_text;
  if (res.polished_scenes && Array.isArray(res.polished_scenes)) {
    polishedText = res.polished_scenes.join('\n\n***\n\n');
  }

  return {
    status: res.status || 'APPROVED',
    feedback: res.feedback || 'No specific feedback provided.',
    polished_text: polishedText || chapter.content
  };
};

export const replanCurrentChapter = async (
  currentPlan: ChapterPlan,
  ledger: Ledger,
  inspectorFeedback: string,
  previousContext: string
): Promise<ChapterPlan> => {
  const prompt = `You are the Lead Producer. The current Chapter Plan is causing a logic error (Conflict: ${inspectorFeedback}). 
Rewrite the Chapter Plan to remove the conflict while preserving the Ledger's Absolute Canon. Ensure the characters act on their existing knowledge.

<ORIGINAL_PLAN>
${JSON.stringify(currentPlan, null, 2)}
</ORIGINAL_PLAN>

<CURRENT_LEDGER>
${JSON.stringify(ledger, null, 2)}
</CURRENT_LEDGER>

<PREVIOUS_CHAPTER_SUMMARY>
${previousContext}
</PREVIOUS_CHAPTER_SUMMARY>

Your task: REWRITE the chapter plan for this single chapter.
The new plan MUST:
1. Strictly respect the Current Ledger.
2. Directly resolve the Inspector's feedback.
3. Provide a logical, achievable bridge from the Previous Chapter Summary.
4. Abandon any plot points from the Original Plan that are currently impossible.
5. If the Ledger has Unresolved Plot Points that MUST happen before the Original Plan can occur, you MUST make those Unresolved Plot Points the focus of this new Chapter Plan.

Return a JSON object matching the ChapterPlan interface:
{
  "chapterNumber": number,
  "title": string,
  "pov": string,
  "summary": string,
  "requiredBeats": string[],
  "forbiddenMoves": string[],
  "continuityNotes": string[],
  "characterFocus": string[],
  "locationFocus": string[]
}
Return ONLY the JSON object. Do not include commentary.`;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.CHAPTER_CONTRACT.model,
    contents: prompt,
    config: { 
      systemInstruction: SYSTEM_INSTRUCTIONS.ARCHITECT,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.CHAPTER_CONTRACT.thinkingLevel },
      temperature: 0.7,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          chapterNumber: { type: Type.NUMBER },
          title: { type: Type.STRING },
          pov: { type: Type.STRING },
          summary: { type: Type.STRING },
          requiredBeats: { type: Type.ARRAY, items: { type: Type.STRING } },
          forbiddenMoves: { type: Type.ARRAY, items: { type: Type.STRING } },
          continuityNotes: { type: Type.ARRAY, items: { type: Type.STRING } },
          characterFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
          locationFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
                  progression: {
                    type: Type.OBJECT,
                    properties: {
                      immediateGoal: { type: Type.STRING },
                      activeOpposition: { type: Type.STRING },
                      protagonistDecision: { type: Type.STRING },
                      decisionCost: { type: Type.STRING },
                      permanentChange: { type: Type.STRING },
                      externalArcAdvance: { type: Type.STRING },
                      internalArcPressure: { type: Type.STRING },
                      relationshipShift: { type: Type.STRING },
                      newComplication: { type: Type.STRING },
                      carryForwardPressure: { type: Type.STRING },
                      cannotResetToPriorState: { type: Type.BOOLEAN }
                    },
                    required: ["immediateGoal", "activeOpposition", "protagonistDecision", "decisionCost", "permanentChange", "externalArcAdvance", "internalArcPressure", "newComplication", "carryForwardPressure", "cannotResetToPriorState"]
                  },
                  characterArcStep: {
                    type: Type.OBJECT,
                    properties: {
                      beliefAtStart: { type: Type.STRING },
                      pressureApplied: { type: Type.STRING },
                      reaction: { type: Type.STRING },
                      evidenceBeliefIsFailing: { type: Type.STRING },
                      beliefAtEnd: { type: Type.STRING }
                    },
                    required: ["beliefAtStart", "pressureApplied", "reaction", "evidenceBeliefIsFailing", "beliefAtEnd"]
                  }
                },
                required: ["chapterNumber", "title", "summary", "requiredBeats", "progression", "characterArcStep"]
      }
    }
  });

  let parsed = extractAndParseJSON(response.text || "{}");
  if (parsed.chapterPlan) parsed = parsed.chapterPlan;
  if (parsed.plan) parsed = parsed.plan;
  
  return {
    ...currentPlan,
    ...parsed
  };
};

export const generateDirectorsNote = async (
  failedDraft: string,
  inspectorFeedback: string,
  ledger: Ledger,
  plan: ChapterPlan
): Promise<string> => {
  const prompt = `You are the AI Director. The Scribe has repeatedly failed to write this chapter.
The Inspector rejected the last draft with this feedback:
<INSPECTOR_FEEDBACK>
${inspectorFeedback}
</INSPECTOR_FEEDBACK>

<CHAPTER_PLAN>
${JSON.stringify(plan, null, 2)}
</CHAPTER_PLAN>

<CURRENT_LEDGER>
${JSON.stringify(ledger, null, 2)}
</CURRENT_LEDGER>

Your task is to write a short, punchy "Director's Note" to the Scribe for their next attempt.
This note must explicitly tell the Scribe what to focus on and what to avoid to satisfy the Inspector.
Example: "The Inspector says Jorek already knows about the sacrifices. Next attempt: Focus ONLY on the tension of the stealth walk. Do not mention the machine's purpose."

Return ONLY the text of the Director's Note.`;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.DIRECTOR_REWRITE.model,
    contents: prompt,
    config: {
      temperature: 0.7,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.DIRECTOR_REWRITE.thinkingLevel }
    }
  });

  return response.text?.trim() || "Follow the Inspector's feedback strictly.";
};

export const rewriteDeadlockedDraft = async (
  failedDraft: string,
  inspectorFeedback: string,
  ledger: Ledger,
  plan: ChapterPlan,
  previousContext: string,
  bible: StoryBible,
  softState?: SoftStateBrief | null
): Promise<Chapter> => {
  const activeLocations = Object.values(ledger.locations || {}).filter((l: any) => l.status?.includes('Active')).map((l: any) => l.name);
  const activeCharacters = Object.values(ledger.characters || {}).filter((c: any) => c.status?.includes('Active') || activeLocations.includes(c.location)).map((c: any) => c.name);

  const softStateContext = softState ? `
<SOFT_STATE_MEMORY>
- Sensory Carryover: ${softState.recentSensoryCarryover}
- Emotional Residue: ${softState.activeEmotionalResidue}
- Voice Notes: ${softState.voiceNotes}
- Environmental Cues: ${softState.environmentalContinuityCues}
- Motifs: ${softState.motifsToLightlyPreserve}
- ANTI-REPETITION: ${softState.antiRepetitionWarnings}
</SOFT_STATE_MEMORY>` : "";

  const prompt = `You are the AI DIRECTOR, stepping in as the human author to fix a critically deadlocked chapter.
The Scribe AI has failed multiple times to write this chapter without breaking continuity.

<INSPECTOR_FEEDBACK>
${inspectorFeedback}
</INSPECTOR_FEEDBACK>

<CURRENT_LEDGER>
${JSON.stringify(ledger, null, 2)}
</CURRENT_LEDGER>
${softStateContext}

<CANON_ANCHORS>
- ACTIVE LOCATIONS: ${activeLocations.length > 0 ? activeLocations.join(', ') : "Unknown"}
- ACTIVE CHARACTERS: ${activeCharacters.length > 0 ? activeCharacters.join(', ') : "Unknown"}
- AVAILABLE CAST: ${Object.values(ledger.characters || {}).map((c: any) => c.name).join(', ')}
- UNRESOLVED PLOT POINTS: ${ledger.plot_points?.join(', ') || "None"}
- RESOLVED PLOT POINTS (DO NOT REPEAT): ${ledger.resolved_plot_points?.join(', ') || "None"}
</CANON_ANCHORS>

STRICT RULE: The characters are currently at the Active Locations. They ALREADY KNOW the Resolved Plot Points. Do NOT write them discovering, theorizing, or being shocked by these facts. Treat these facts as mundane background knowledge.

<PREVIOUS_CHAPTER_SUMMARY>
${previousContext}
</PREVIOUS_CHAPTER_SUMMARY>

<CHAPTER_PLAN>
TITLE: ${plan.title}
SUMMARY: ${plan.summary}
REQUIRED BEATS:
${(plan.requiredBeats || []).map(b => `- ${b}`).join('\n')}
CHARACTER FOCUS: ${(plan.characterFocus || []).join(', ')}
LOCATION FOCUS: ${(plan.locationFocus || []).join(', ')}
CONTINUITY NOTES: ${(plan.continuityNotes || []).join(', ')}
FORBIDDEN MOVES: ${(plan.forbiddenMoves || []).join(', ')}
</CHAPTER_PLAN>

<FAILED_DRAFT>
${failedDraft}
</FAILED_DRAFT>

Your task is to perform a TOTAL STRUCTURAL REWRITE of the <FAILED_DRAFT>.
You must:
1. RUTHLESSLY DELETE any paragraphs, scenes, or dialogue that violate the Inspector's feedback (e.g., teleporting characters, invented abilities, bad cutaways, omniscient knowledge).
2. IF THE SCRIBE SKIPPED A CRITICAL ON-SCREEN EVENT (like a kidnapping or battle), you MUST write that event happening in real-time. Do NOT use exposition or "video calls" to summarize it. Show it happening.
3. Rewrite the narrative to STRICTLY adhere to the Current Ledger. If a character isn't at the location, remove them. If an event already happened, do not repeat it.
4. EXECUTE THE TYPED CHAPTER PLAN, EXCEPT WHEN IT CONFLICTS WITH THE INSPECTOR'S FEEDBACK. IF THE INSPECTOR SAYS THE PLAN IS WRONG, THE INSPECTOR WINS. The plan contains typed beats. You must handle them accordingly:
   - [EXACT FACTS]: Must be executed literally, exactly as written. No deviation. Do not invent unlisted mechanics or tools.
   - [REQUIRED OUTCOMES]: The end result must happen, but you can invent the staging/action to get there.
   - [OPTIONAL STAGING]: Suggestions for action/dialogue. You can use them or invent your own if it fits better.
   - [EMOTIONAL PAYOFF ONLY]: Focus on the internal/emotional realization. The physical action doesn't matter as much.
   - [FORBIDDEN REPEAT]: CRITICAL! Do NOT write this happening. It is a trap. Do not summarize it. Do not mention it. It already happened.
5. If the <FAILED_DRAFT> is fundamentally broken, discard its structure entirely and write a completely new chapter from scratch that satisfies the plan and ledger, bridging logically from the <PREVIOUS_CHAPTER_SUMMARY>.
6. ONLY USE ACTIVE CHARACTERS IN ACTIVE LOCATIONS. Do not cut away to other locations unless explicitly required by the Chapter Plan.

STYLE GUIDE: ${bible.style_guide}
${HUMAN_PROSE_CONSTRAINT}
LENGTH: ${bible.chapterLength}

Return a JSON object:
{
  "chapter": {
    "title": "Chapter Title",
    "content": "The full, rewritten text of the chapter. Ensure it is detailed, immersive, and meets the length requirement.",
    "summary": "A brief summary of the events in this rewritten chapter."
  }
}`;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.DIRECTOR_REWRITE.model,
    contents: prompt,
    config: {
      temperature: 0.6, // Increased slightly to allow for full structural rewrites
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.DIRECTOR_REWRITE.thinkingLevel },
      maxOutputTokens: 16384,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          chapter: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              content: { type: Type.STRING },
              summary: { type: Type.STRING }
            },
            required: ["title", "content", "summary"]
          }
        },
        required: ["chapter"]
      }
    }
  });

  let json = extractAndParseJSON(response.text || "");
  if (json.chapter) json = json.chapter;
  if (json.draft) json = json.draft;
  
  if (!json.content) {
    json.content = "Error: Model failed to generate content.";
  }
  
  return { id: plan.chapterNumber, ...json };
};

export const publishChapter = async (
  chapter: Chapter,
  bible: StoryBible,
  ledger: Ledger,
  plan?: ChapterPlan
): Promise<PublicationEditorResult> => {
  const planContext = plan ? `TITLE: ${plan.title}
SUMMARY: ${plan.summary}
REQUIRED BEATS:
${(plan.requiredBeats || []).map(b => `- ${b}`).join('\n')}
CHARACTER FOCUS: ${(plan.characterFocus || []).join(', ')}
LOCATION FOCUS: ${(plan.locationFocus || []).join(', ')}
CONTINUITY NOTES: ${(plan.continuityNotes || []).join(', ')}
FORBIDDEN MOVES: ${(plan.forbiddenMoves || []).join(', ')}` : "No specific plan provided.";

  const prompt = `
    PUBLICATION REVIEW REQUEST
    
    CHAPTER DRAFT:
    ${chapter.content}
    
    CHAPTER PLAN:
    ${planContext}
    
    STORY BIBLE (RAILS):
    TITLE: ${bible.title}
    PREMISE: ${JSON.stringify(bible.premise)}
    STYLE GUIDE: ${bible.style_guide}
    ${HUMAN_PROSE_CONSTRAINT}
    
    CANON STATE (LEDGER):
    ${JSON.stringify(ledger)}
    
    SERIES CONSTRAINTS:
    MODE: ${bible.mode}
    CHAPTERS: ${bible.targetChapterCount}
    LENGTH: ${bible.chapterLength}
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.PUBLICATION_EDITOR.model,
    contents: prompt,
    config: {
      systemInstruction: SYSTEM_INSTRUCTIONS.PUBLICATION_EDITOR,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.PUBLICATION_EDITOR.thinkingLevel },
      maxOutputTokens: 65536,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          patched_scenes: {
            type: Type.ARRAY,
            description: "The patched scenes. Maintain the length of the original scenes. Do not summarize or shorten them.",
            items: { type: Type.STRING }
          },
          change_log: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                issue: { type: Type.STRING },
                fix: { type: Type.STRING },
                location: { type: Type.STRING }
              }
            }
          },
          lore_compliance: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                rail: { type: Type.STRING },
                status: { type: Type.STRING },
                note: { type: Type.STRING }
              }
            }
          },
          architect_queries: {
            type: Type.ARRAY,
            items: { type: Type.STRING }
          }
        },
        required: ["patched_scenes", "change_log", "lore_compliance"]
      }
    },
  });

  const res = extractAndParseJSON(response.text || "{}");
  
  if (res.patched_scenes && Array.isArray(res.patched_scenes)) {
    res.patched_content = res.patched_scenes.join('\n\n***\n\n');
    delete res.patched_scenes;
  }
  
  res.change_log = Array.isArray(res.change_log) ? res.change_log : [];
  res.lore_compliance = Array.isArray(res.lore_compliance) ? res.lore_compliance : [];
  
  return res;
};

export const updateLedger = async (chapter: Chapter, currentLedger: Ledger, mode: StoryMode): Promise<Ledger> => {
  const instruction = SYSTEM_INSTRUCTIONS.ARCHIVIST + `\nAnalyze this ${mode} chapter and update the factual ledger. Move any completed plot_points into resolved_plot_points.`;
  const response = await generateWithRetry({
    model: WORKER_CONFIG.ARCHIVIST.model,
    contents: `CHAPTER CONTENT:\n${chapter.content}\n\nCURRENT LEDGER:\n${JSON.stringify(currentLedger)}`,
    config: {
      systemInstruction: instruction,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHIVIST.thinkingLevel },
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          characters: { type: Type.OBJECT },
          locations: { type: Type.OBJECT },
          inventory: { type: Type.ARRAY, items: { type: Type.STRING } },
          plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
          resolved_plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
          momentum: { type: Type.STRING },
          arc_stage: { type: Type.STRING },
          latestStateDelta: {
            type: Type.OBJECT,
            properties: {
              gained: { type: Type.ARRAY, items: { type: Type.STRING } },
              lost: { type: Type.ARRAY, items: { type: Type.STRING } },
              damaged: { type: Type.ARRAY, items: { type: Type.STRING } },
              revealed: { type: Type.ARRAY, items: { type: Type.STRING } },
              relationshipsChanged: { type: Type.ARRAY, items: { type: Type.STRING } },
              goalsChanged: { type: Type.ARRAY, items: { type: Type.STRING } },
              threatsEscalated: { type: Type.ARRAY, items: { type: Type.STRING } },
              unresolvedConsequences: { type: Type.ARRAY, items: { type: Type.STRING } }
            },
            required: ["gained", "lost", "damaged", "revealed", "relationshipsChanged", "goalsChanged", "threatsEscalated", "unresolvedConsequences"]
          }
        },
        required: ["characters", "locations", "inventory", "plot_points", "momentum", "arc_stage", "latestStateDelta"]
      }
    },
  });

  const rawData = extractAndParseJSON(response.text || "{}");
  
  const inventory = Array.isArray(rawData.inventory) && rawData.inventory.length > 0 ? rawData.inventory : currentLedger.inventory || [];
  const plot_points = Array.isArray(rawData.plot_points) && rawData.plot_points.length > 0 ? rawData.plot_points : currentLedger.plot_points || [];
  const resolved_plot_points = Array.isArray(rawData.resolved_plot_points) && rawData.resolved_plot_points.length > 0 ? rawData.resolved_plot_points : currentLedger.resolved_plot_points || [];

  return {
    ...currentLedger,
    ...rawData,
    inventory,
    plot_points,
    resolved_plot_points,
    characters: {
      ...(currentLedger.characters || {}),
      ...(rawData.characters || {})
    },
    locations: {
      ...(currentLedger.locations || {}),
      ...(rawData.locations || {})
    },
    last_updated: new Date().toISOString()
  };
};

export const updateLedgerFromNote = async (note: string, currentLedger: Ledger): Promise<Ledger> => {
  const instruction = SYSTEM_INSTRUCTIONS.ARCHIVIST + `\nAnalyze this Director's Note and update the factual ledger to reflect these changes in the story state. Ensure continuity is maintained while incorporating the new direction.`;
  const response = await generateWithRetry({
    model: WORKER_CONFIG.ARCHIVIST.model,
    contents: `DIRECTOR'S NOTE:\n${note}\n\nCURRENT LEDGER:\n${JSON.stringify(currentLedger)}`,
    config: {
      systemInstruction: instruction,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHIVIST.thinkingLevel },
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          characters: { type: Type.OBJECT },
          locations: { type: Type.OBJECT },
          inventory: { type: Type.ARRAY, items: { type: Type.STRING } },
          plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
          resolved_plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
          momentum: { type: Type.STRING },
          arc_stage: { type: Type.STRING },
          latestStateDelta: {
            type: Type.OBJECT,
            properties: {
              gained: { type: Type.ARRAY, items: { type: Type.STRING } },
              lost: { type: Type.ARRAY, items: { type: Type.STRING } },
              damaged: { type: Type.ARRAY, items: { type: Type.STRING } },
              revealed: { type: Type.ARRAY, items: { type: Type.STRING } },
              relationshipsChanged: { type: Type.ARRAY, items: { type: Type.STRING } },
              goalsChanged: { type: Type.ARRAY, items: { type: Type.STRING } },
              threatsEscalated: { type: Type.ARRAY, items: { type: Type.STRING } },
              unresolvedConsequences: { type: Type.ARRAY, items: { type: Type.STRING } }
            },
            required: ["gained", "lost", "damaged", "revealed", "relationshipsChanged", "goalsChanged", "threatsEscalated", "unresolvedConsequences"]
          }
        },
        required: ["characters", "locations", "inventory", "plot_points", "momentum", "arc_stage", "latestStateDelta"]
      }
    },
  });

  const rawData = extractAndParseJSON(response.text || "{}");
  
  const inventory = Array.isArray(rawData.inventory) && rawData.inventory.length > 0 ? rawData.inventory : currentLedger.inventory || [];
  const plot_points = Array.isArray(rawData.plot_points) && rawData.plot_points.length > 0 ? rawData.plot_points : currentLedger.plot_points || [];
  const resolved_plot_points = Array.isArray(rawData.resolved_plot_points) && rawData.resolved_plot_points.length > 0 ? rawData.resolved_plot_points : currentLedger.resolved_plot_points || [];

  return {
    ...currentLedger,
    ...rawData,
    inventory,
    plot_points,
    resolved_plot_points,
    characters: {
      ...(currentLedger.characters || {}),
      ...(rawData.characters || {})
    },
    locations: {
      ...(currentLedger.locations || {}),
      ...(rawData.locations || {})
    },
    last_updated: new Date().toISOString()
  };
};

// Add to your validation logic
function validateSpatialMath(metadata?: { waterDepth?: number, obstacleHeight?: number, action?: string }) {
  if (!metadata) return null;
  const waterLevel = metadata.waterDepth || 0;
  const objectHeight = metadata.obstacleHeight || 0;
  
  if (waterLevel > 0 && objectHeight > 0) {
    const clearance = objectHeight - waterLevel;
    if (clearance < 1.5 && metadata.action === "ducking") {
      return { 
        status: "FAIL", 
        reason: `Spatial Geometry Error: ${clearance}ft clearance is insufficient for a human head (requires 1.5ft).` 
      };
    }
  }
  return null;
}

export const validateChapter = async (
  chapter: Chapter, 
  ledger: Ledger, 
  bible: StoryBible,
  directorsNote?: string, 
  retryCount: number = 0,
  plan?: ChapterPlan
): Promise<ValidationResult> => {
  const isIntervention = retryCount >= 2;
  const isPrescriptive = retryCount >= 4;
  
  const systemInstruction = SYSTEM_INSTRUCTIONS.INSPECTOR + 
    `\n\nSTYLE GUIDE: ${bible.style_guide}\n\n` +
    "STRICT HIERARCHY OF TRUTH:\n1. MASTER LEDGER (Absolute Canon)\n2. DIRECTOR'S NOTE (Immediate Priority)\n3. CHAPTER PLAN (Required Beats)\n\n" +
    "CRITICAL RULE: Never suggest a perspective shift or location change if the Ledger or Director's Note specifies a 'Location Lock' or 'Scene Lock'.\n" +
    "Ensure the Scribe followed the CHAPTER CONTRACT: no invented abilities, no teleportation, no repeating resolved beats, no bad cutaways.\n" +
    "SOFT STATE AWARENESS: The Scribe may have used 'Soft State Memory' to maintain atmospheric continuity. Soft state is supportive, not authoritative. Validate that it did not cause canon drift or over-repetition." +
    (directorsNote ? "\nSpecial Task: Ensure the Director's Note was followed correctly." : "") +
    (isIntervention ? "\nCRITICAL INTERVENTION: The Scribe is failing to maintain continuity. You MUST provide a 'Mandatory Scene Outline' in the correction_order field." : "") +
    (isPrescriptive ? "\nPRESCRIPTIVE MODE ACTIVE: You must also provide a 'STRICT SCENE CAST' (list of allowed characters) and 'SPATIAL CONSTRAINTS' (forbidden locations) in your correction_order. Be extremely literal." : "");

  const planContext = plan ? `CHAPTER PLAN:
TITLE: ${plan.title}
SUMMARY: ${plan.summary}
REQUIRED BEATS:
${(plan.requiredBeats || []).map(b => `- ${b}`).join('\n')}
CHARACTER FOCUS: ${(plan.characterFocus || []).join(', ')}
LOCATION FOCUS: ${(plan.locationFocus || []).join(', ')}
CONTINUITY NOTES: ${(plan.continuityNotes || []).join(', ')}
FORBIDDEN MOVES: ${(plan.forbiddenMoves || []).join(', ')}` : "";

  const response = await generateWithRetry({
    model: WORKER_CONFIG.INSPECTOR.model,
    contents: `INSPECT CONTINUITY:
Chapter: ${chapter.content}
Ledger: ${JSON.stringify(ledger)}
${planContext}
${directorsNote ? `DIRECTOR'S NOTE TO VERIFY: ${directorsNote}` : ""}
${isIntervention ? "STATUS: REPEATED FAILURE DETECTED. GENERATE MANDATORY OUTLINE." : ""}`,
    config: {
      systemInstruction,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.INSPECTOR.thinkingLevel },
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          continuity: {
            type: Type.OBJECT,
            properties: {
              status: { type: Type.STRING },
              reason: { type: Type.STRING },
              correction_order: { type: Type.STRING }
            },
            required: ["status", "reason", "correction_order"]
          },
          progression: {
            type: Type.OBJECT,
            properties: {
              passed: { type: Type.BOOLEAN },
              goalPresent: { type: Type.BOOLEAN },
              activeOppositionPresent: { type: Type.BOOLEAN },
              protagonistMakesDecision: { type: Type.BOOLEAN },
              meaningfulCostPresent: { type: Type.BOOLEAN },
              permanentChangePresent: { type: Type.BOOLEAN },
              externalArcAdvanced: { type: Type.BOOLEAN },
              internalArcPressured: { type: Type.BOOLEAN },
              nextChapterPressureCreated: { type: Type.BOOLEAN },
              statusQuoResetDetected: { type: Type.BOOLEAN },
              correctionOrder: { type: Type.STRING }
            },
            required: ["passed", "goalPresent", "activeOppositionPresent", "protagonistMakesDecision", "meaningfulCostPresent", "permanentChangePresent", "externalArcAdvanced", "internalArcPressured", "nextChapterPressureCreated", "statusQuoResetDetected"]
          },
          density: {
            type: Type.OBJECT,
            properties: {
              actionDecisionBeats: { type: Type.NUMBER },
              conflictEscalationBeats: { type: Type.NUMBER },
              emotionalChangeBeats: { type: Type.NUMBER },
              consequenceBeats: { type: Type.NUMBER },
              descriptiveOnlyParagraphs: { type: Type.NUMBER },
              repeatedDetailSignals: { type: Type.ARRAY, items: { type: Type.STRING } }
            },
            required: ["actionDecisionBeats", "conflictEscalationBeats", "emotionalChangeBeats", "consequenceBeats", "descriptiveOnlyParagraphs", "repeatedDetailSignals"]
          },
          spatialMetadata: {
            type: Type.OBJECT,
            properties: {
              waterDepth: { type: Type.NUMBER, description: "Depth of water in feet, if mentioned" },
              obstacleHeight: { type: Type.NUMBER, description: "Height of obstacle from floor in feet, if mentioned" },
              action: { type: Type.STRING, description: "Action being performed, e.g., 'ducking', 'crawling'" }
            }
          }
        },
        required: ["continuity", "progression", "density"]
      }
    },
  });

  const rawRes = extractAndParseJSON(response.text || "");
  const continuity = rawRes.continuity || { status: 'FAIL', reason: 'Missing continuity validation', correction_order: '' };
  const progression = rawRes.progression || { passed: false };
  const density = rawRes.density || { descriptiveOnlyParagraphs: 0 };
  
  // 3. The "Spatial Check" Utility
  const spatialError = validateSpatialMath(rawRes.spatialMetadata);
  if (spatialError) {
    return {
      status: 'FAIL',
      reason: spatialError.reason,
      correction_order: "MANDATORY GEOMETRY FIX: " + spatialError.reason + " You MUST rewrite the scene to either lower the water level or raise the obstacle to provide at least 1.5ft of clearance."
    };
  }

  let finalStatus: 'PASS' | 'FAIL' = continuity.status === 'PASS' && progression.passed ? 'PASS' : 'FAIL';
  
  if (finalStatus === 'PASS' && density.descriptiveOnlyParagraphs >= 3 && density.actionDecisionBeats === 0) {
    finalStatus = 'FAIL';
  }

  let finalReason = continuity.reason || 'No specific issues detected.';
  let finalCorrection = continuity.correction_order || '';

  if (continuity.status !== 'FAIL' && !progression.passed) {
    finalReason = "Progression Validation Failed: The chapter lacks meaningful narrative change.";
    finalCorrection = progression.correctionOrder || "Ensure the chapter creates irreversible consequence for the next chapter.";
  }

  if (finalStatus === 'FAIL' && density.descriptiveOnlyParagraphs >= 3 && density.actionDecisionBeats === 0) {
    if (continuity.status !== 'FAIL' && progression.passed) {
        finalReason = "Descriptive Density Warning: Too many paragraphs of description without action or decision.";
        finalCorrection = "Trim static description and ensure characters are making decisions.";
    } else {
        finalReason += "\nAlso failed Density: Too many paragraphs of description.";
    }
  }

  return {
    status: finalStatus,
    reason: finalReason,
    correction_order: finalCorrection,
    progression,
    density
  } as ValidationResult;
};

export const rewriteChapter = async (
  chapter: Chapter,
  ledger: Ledger,
  instructions: string,
  bible: StoryBible,
  previousContext?: string,
  plan?: ChapterPlan
): Promise<any> => {
  const activeLocations = Object.values(ledger.locations || {}).filter((l: any) => l.status?.includes('Active')).map((l: any) => `${l.name}: ${l.desc}`).join('\n');
  const activeCharacters = Object.values(ledger.characters || {}).filter((c: any) => c.status?.includes('Active')).map((c: any) => `${c.name}: ${c.desc}`).join('\n');

  const planContext = plan ? `CHAPTER PLAN:
TITLE: ${plan.title}
SUMMARY: ${plan.summary}
REQUIRED BEATS:
${(plan.requiredBeats || []).map(b => `- ${b}`).join('\n')}
CHARACTER FOCUS: ${(plan.characterFocus || []).join(', ')}
LOCATION FOCUS: ${(plan.locationFocus || []).join(', ')}
CONTINUITY NOTES: ${(plan.continuityNotes || []).join(', ')}
FORBIDDEN MOVES: ${(plan.forbiddenMoves || []).join(', ')}` : "None";

  const prompt = `
    ### INPUT STRUCTURE:
    - [PAST SCENE SUMMARY]: ${previousContext || "None provided."}
    - [ACTIVE LORE]: 
      LOCATIONS:
      ${activeLocations || "None"}
      ACTIVE CHARACTERS:
      ${activeCharacters || "None"}
      AVAILABLE CAST:
      ${Object.values(ledger.characters || {}).map((c: any) => c.name).join(', ')}
    - [MANUSCRIPT DRAFT]: 
      ${chapter.content}
    - [CHAPTER PLAN]: 
      ${planContext}
    - [CHAPTER GOAL]: ${plan ? plan.summary : instructions}
    - [STYLE GUIDE]: ${bible.style_guide}
    ${HUMAN_PROSE_CONSTRAINT}
    
    REWRITE INSTRUCTIONS:
    "${instructions}"
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.DIRECTOR_REWRITE.model,
    contents: prompt,
    config: {
      systemInstruction: SYSTEM_INSTRUCTIONS.REWRITER,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.DIRECTOR_REWRITE.thinkingLevel },
      maxOutputTokens: 65536,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          status: { type: Type.STRING },
          failure_reason: { type: Type.STRING },
          scenes: {
            type: Type.ARRAY,
            items: { type: Type.STRING }
          },
          canon_delta: { type: Type.STRING },
          editor_notes: { type: Type.STRING }
        },
        required: ["status", "scenes", "canon_delta", "editor_notes"]
      }
    },
  });

  const json = extractAndParseJSON(response.text || "{}");
  
  if (json.scenes && Array.isArray(json.scenes)) {
    json.content = json.scenes.join('\n\n***\n\n');
    delete json.scenes;
  } else if (!json.content && json.status === "PASS") {
    json.content = "Error: Model failed to generate rewritten content.";
  }
  
  return json;
};

export const generateSpeech = async (text: string): Promise<string | null> => {
  const ai = getAI();
  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash-preview-tts",
      contents: [{ parts: [{ text }] }],
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: {
          voiceConfig: {
            prebuiltVoiceConfig: { voiceName: 'Kore' },
          },
        },
      },
    });
    return response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data || null;
  } catch (error) {
    console.error("TTS Generation Error:", error);
    return null;
  }
};
